import React, { useEffect } from "react";
import { useRewardsBrazePlacement } from "../../hooks/useRewardsBrazePlacement";
import { useBrazeBannerSlotLayout } from "../../hooks/useBrazeBannerSlotLayout";
import slotStyles from "./brazeBannerSlot.module.scss";

export type BrazeBannerSlotProps = {
  placementId: string;
  logLabel: string;
  className: string;
  ariaLabel: string;
  onShowChange?: (show: boolean) => void;
  /** When false, height is scoped to the slot element only. */
  syncGlobalLayout?: boolean;
  /** Smooth height expand on first measure (inline slots only). Defaults to !syncGlobalLayout. */
  animateHeight?: boolean;
  /** When false, global layout vars clear but DOM stays mounted (persistent footer/top). */
  placementActive?: boolean;
  /** Keep slot DOM mounted while Braze loads — avoids remount flicker. */
  keepMounted?: boolean;
};

/**
 * Generic Braze banner slot — cache + measure only, no hardcoded height guesses.
 */
const BrazeBannerSlot: React.FC<BrazeBannerSlotProps> = ({
  placementId,
  logLabel,
  className,
  ariaLabel,
  onShowChange,
  syncGlobalLayout = true,
  animateHeight,
  placementActive = true,
  keepMounted = false,
}) => {
  const { containerRef, showBrazeBanner, placementId: activePlacementId } =
    useRewardsBrazePlacement(placementId, logLabel);

  const shouldAnimate = animateHeight ?? !syncGlobalLayout;

  useBrazeBannerSlotLayout({
    placementId,
    containerRef,
    showBrazeBanner,
    syncGlobalLayout,
    animateHeight: shouldAnimate,
    placementActive,
    keepMounted,
  });

  useEffect(() => {
    onShowChange?.(showBrazeBanner && placementActive);
  }, [showBrazeBanner, placementActive, onShowChange]);

  if (!keepMounted && !showBrazeBanner) return null;

  return (
    <div
      ref={containerRef}
      className={`${className} ${
        shouldAnimate ? slotStyles.brazeBannerSlotAnimated : slotStyles.brazeBannerSlotMeasured
      }`}
      data-braze-banner-placement={activePlacementId}
      aria-label={ariaLabel}
      aria-hidden={!placementActive || !showBrazeBanner}
    />
  );
};

export default BrazeBannerSlot;
